import { useState, useRef, useEffect, useCallback } from 'react'
import { useGoogleMapsScript } from '../hooks/useGoogleMapsScript'

interface AddressAutocompleteProps {
  value: string
  onChange: (address: string) => void
  placeholder?: string
  id?: string
  /**
   * Called when the user picks a suggestion and place details load successfully.
   * Use to auto-fill structured address fields (street, suburb, postcode, state).
   */
  onPlaceResolved?: (parsed: ParsedAuPlaceAddress) => void
  /**
   * Called with the verified address string after the user picks a suggestion that
   * resolves to a real Google Place. Use to gate submission so free-text entries
   * (typed/pasted without selecting) cannot be saved. Not called when resolution
   * fails or a suggestion has no placeId.
   */
  onAddressVerified?: (verifiedAddress: string) => void
  ariaInvalid?: boolean
  ariaDescribedBy?: string
}

interface Suggestion {
  placeId: string
  description: string
}

/** Structured AU address fields from a Google Places result. */
export type ParsedAuPlaceAddress = {
  street: string
  suburb: string
  postcode: string
  /** Uppercase AU state code for form selects (NSW, VIC, …) or empty if unknown. */
  state: string
  /** Single-line display for the search field (matches previous formatted behaviour). */
  formattedLine: string
}

const KNOWN_AU_STATE_CODES = new Set(['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'])

const AU_STATE_LONG_TO_CODE: Record<string, string> = {
  'NEW SOUTH WALES': 'NSW',
  VICTORIA: 'VIC',
  QUEENSLAND: 'QLD',
  'WESTERN AUSTRALIA': 'WA',
  'SOUTH AUSTRALIA': 'SA',
  TASMANIA: 'TAS',
  'AUSTRALIAN CAPITAL TERRITORY': 'ACT',
  'NORTHERN TERRITORY': 'NT',
}

/**
 * Maps Google administrative_area_level_1 text to an AU state select value.
 */
export function normalizeAuStateCode(shortText: string, longText: string): string {
  const s = (shortText || '').trim().toUpperCase()
  if (KNOWN_AU_STATE_CODES.has(s)) return s
  const l = (longText || '').trim().toUpperCase()
  if (KNOWN_AU_STATE_CODES.has(l)) return l
  return AU_STATE_LONG_TO_CODE[l] ?? ''
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Google `formattedAddress` often collapses hyphenated street numbers (e.g. `33-35`)
 * to the first number (`33`) while autocomplete / the user's line still shows the full
 * range. Merge that range into the resolved one-line address so parking submissions
 * match what the user selected.
 *
 * Handles:
 * - `4/33-35 Foo St` vs `4/33 Foo St` (unit + range after slash)
 * - `33-35 Foo St` vs `33 Foo St` (range at line start, no slash)
 */
export function mergeHyphenatedStreetNumberFromHint(formattedLine: string, hint: string): string {
  const formatted = (formattedLine ?? '').trim()
  const h = (hint ?? '').trim()
  if (!formatted || !h) return formatted

  const firstSeg = (line: string) => {
    const m = line.match(/^([^,]+)/)
    return m ? m[1].trim() : line.trim()
  }

  const origFormHead = firstSeg(formatted)
  const formHead = origFormHead
  const userHead = firstSeg(h)
  if (!formHead || !userHead) return formatted

  // Unit (or level) prefix then hyphenated lot/street number: `4/33-35 …` → `4/33 …`
  const slashRange = userHead.match(/^(.+?\/)(\d+-\d+)(\s.*)$/)
  if (slashRange) {
    const prefix = slashRange[1]
    const fullRange = slashRange[2]
    const startNum = fullRange.split('-')[0] ?? ''
    if (
      startNum &&
      userHead.includes(prefix + fullRange) &&
      (formHead.startsWith(prefix + startNum + ' ') ||
        formHead.startsWith(prefix + startNum + ',') ||
        formHead === prefix + startNum)
    ) {
      if (!formHead.includes(fullRange)) {
        const re = new RegExp('^' + escapeRegExp(prefix + startNum) + '(?=\\s|,|$)')
        const newHead = formHead.replace(re, prefix + fullRange)
        if (newHead !== formHead) {
          return formatted.replace(origFormHead, newHead)
        }
      }
    }
  }

  // Leading hyphenated street number without a unit slash
  if (!userHead.includes('/')) {
    const lead = userHead.match(/^(\d+-\d+)(\s.+)$/)
    if (lead) {
      const fullRange = lead[1]
      const startNum = fullRange.split('-')[0] ?? ''
      if (
        startNum &&
        (formHead === startNum ||
          formHead.startsWith(startNum + ' ') ||
          formHead.startsWith(startNum + ','))
      ) {
        if (!formHead.startsWith(fullRange)) {
          const re = new RegExp('^' + escapeRegExp(startNum) + '(?=\\s|,|$)')
          const newHead = formHead.replace(re, fullRange)
          if (newHead !== formHead) {
            return formatted.replace(origFormHead, newHead)
          }
        }
      }
    }
  }

  return formatted
}

/**
 * Reads Google Places address components into street / suburb / postcode / state.
 *
 * The display line (`formattedLine`) is always taken from `place.formattedAddress`
 * (minus a trailing ", Australia") so unit/level prefixes Google returns in
 * `subpremise` are preserved verbatim — reconstructing from street_number + route
 * silently dropped them.
 */
export function parsePlaceToAuAddress(place: google.maps.places.Place): ParsedAuPlaceAddress {
  const rawFormatted = place.formattedAddress
  const strippedFormatted =
    typeof rawFormatted === 'string' ? rawFormatted.replace(/,\s*Australia\s*$/i, '').trim() : ''
  const hasFormattedAddress = strippedFormatted.length > 0
  let formattedLine = hasFormattedAddress ? strippedFormatted : ''

  if (!hasFormattedAddress) {
    const placeId =
      typeof (place as { id?: unknown }).id === 'string' ? (place as { id: string }).id : undefined
    console.warn(
      '[AddressAutocomplete] Place missing formattedAddress; using component-derived line or empty display.',
      {
        placeId: placeId ?? '(unknown)',
        hasAddressComponents: !!(place.addressComponents && Array.isArray(place.addressComponents)),
      }
    )
  }

  const components = place.addressComponents
  if (!components || !Array.isArray(components)) {
    const loose = parseLooseStatePostcodeFromLine(formattedLine)
    return {
      street: '',
      suburb: '',
      postcode: loose.postcode,
      state: loose.state,
      formattedLine,
    }
  }

  const get = (typeGroups: string[]) => {
    const c = components.find((ac) => typeGroups.some((t) => ac.types.includes(t)))
    if (!c) return { long: '', short: '' }
    return {
      long: (c.longText ?? '').trim(),
      short: (c.shortText ?? '').trim(),
    }
  }

  const subpremise = get(['subpremise'])
  const streetNumber = get(['street_number'])
  const route = get(['route'])
  const streetNumberPart = [streetNumber.long || streetNumber.short, route.long || route.short]
    .filter(Boolean)
    .join(' ')
    .trim()
  const subpremiseText = subpremise.long || subpremise.short
  const street = subpremiseText
    ? streetNumberPart
      ? `${subpremiseText}/${streetNumberPart}`
      : subpremiseText
    : streetNumberPart

  const locality = get(['locality'])
  const postalTown = get(['postal_town'])
  const subloc = get(['sublocality', 'sublocality_level_1'])
  const suburb =
    locality.long ||
    locality.short ||
    postalTown.long ||
    postalTown.short ||
    subloc.long ||
    subloc.short ||
    ''

  const pc = get(['postal_code'])
  const postcodeRaw = pc.long || pc.short || ''
  const postcode = postcodeRaw.replace(/\D/g, '').slice(0, 4)

  const adm = get(['administrative_area_level_1'])
  const state = normalizeAuStateCode(adm.short || adm.long, adm.long || adm.short)

  if (!hasFormattedAddress) {
    const statePostcode = [state, postcode].filter(Boolean).join(' ')
    formattedLine = [street, suburb, statePostcode].filter(Boolean).join(', ')
  }

  return { street, suburb, postcode, state, formattedLine }
}

function parseLooseStatePostcodeFromLine(line: string): { state: string; postcode: string } {
  const m = line.match(/\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)\s+(\d{4})\b/i)
  if (!m) return { state: '', postcode: '' }
  return { state: m[1].toUpperCase(), postcode: m[2] }
}

export default function AddressAutocomplete({
  value,
  onChange,
  placeholder = 'Enter parking address',
  id,
  onPlaceResolved,
  onAddressVerified,
  ariaInvalid,
  ariaDescribedBy,
}: AddressAutocompleteProps) {
  const { isLoaded, loadError } = useGoogleMapsScript()

  if (!isLoaded || loadError) {
    const loadingId = id ? `${id}-loading` : 'address-autocomplete-loading'
    const describedBy = [ariaDescribedBy, !isLoaded && !loadError ? loadingId : null]
      .filter(Boolean)
      .join(' ') || undefined

    return (
      <div className="relative w-full">
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              e.currentTarget.blur()
            }
          }}
          className="flex-1 font-body font-regular text-md leading-md text-text-primary-900 outline-none border-none bg-transparent w-full"
          placeholder={placeholder}
          aria-invalid={ariaInvalid}
          aria-describedby={describedBy}
          aria-busy={!loadError && !isLoaded}
        />
        {!isLoaded && !loadError && (
          <p
            id={loadingId}
            className="mt-1 font-body text-xs text-text-tertiary-600"
          >
            Loading address search…
          </p>
        )}
      </div>
    )
  }

  return (
    <AutocompleteInput
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      id={id}
      onPlaceResolved={onPlaceResolved}
      onAddressVerified={onAddressVerified}
      ariaInvalid={ariaInvalid}
      ariaDescribedBy={ariaDescribedBy}
    />
  )
}

function AutocompleteInput({
  value,
  onChange,
  placeholder,
  id,
  onPlaceResolved,
  onAddressVerified,
  ariaInvalid,
  ariaDescribedBy,
}: AddressAutocompleteProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState(value)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [isFetching, setIsFetching] = useState(false)
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastInternalValueRef = useRef(value)

  // Create a session token on mount
  useEffect(() => {
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken()
  }, [])

  // Sync external value when not actively typing
  useEffect(() => {
    // Only sync if the external value has changed since the last internal update
    if (value !== lastInternalValueRef.current) {
      setInputValue(value)
      lastInternalValueRef.current = value
    }
  }, [value])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setSuggestions([])
        setHighlightedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchSuggestions = useCallback(async (input: string) => {
    if (input.length < 3) {
      setSuggestions([])
      setHighlightedIndex(-1)
      setIsFetching(false)
      return
    }

    setIsFetching(true)
    try {
      const { suggestions: results } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: sessionTokenRef.current!,
          includedRegionCodes: ['au'],
        })

      setSuggestions(
        results.map((s) => ({
          placeId: s.placePrediction?.placeId ?? '',
          description: s.placePrediction?.text?.text ?? '',
        }))
      )
      setHighlightedIndex(-1)
    } catch (err) {
      console.warn('[AddressAutocomplete] fetchAutocompleteSuggestions failed', err)
      setSuggestions([])
      setHighlightedIndex(-1)
    } finally {
      setIsFetching(false)
    }
  }, [])

  // If the user typed before Places finished loading, we mount with text already in the
  // field but no change event — fetch suggestions for the existing value.
  useEffect(() => {
    if (inputValue.length >= 3) {
      void fetchSuggestions(inputValue)
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only
  }, [])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)
    onChange(val)
    lastInternalValueRef.current = val

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(val)
    }, 300)
  }

  const handleSelect = useCallback(
    async (placeId: string, fallbackDescription: string) => {
      setSuggestions([])
      setHighlightedIndex(-1)
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken()

      if (!placeId) {
        setInputValue(fallbackDescription)
        onChange(fallbackDescription)
        lastInternalValueRef.current = fallbackDescription
        return
      }

      try {
        const { Place } = (await google.maps.importLibrary(
          'places'
        )) as google.maps.PlacesLibrary
        const place = new Place({ id: placeId })
        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress'],
        })
        const parsed = parsePlaceToAuAddress(place)
        const typedHint = lastInternalValueRef.current || ''
        let displayAddress = parsed.formattedLine || fallbackDescription
        if (parsed.formattedLine) {
          displayAddress = mergeHyphenatedStreetNumberFromHint(displayAddress, typedHint)
          displayAddress = mergeHyphenatedStreetNumberFromHint(displayAddress, fallbackDescription)
        }
        onPlaceResolved?.({ ...parsed, formattedLine: displayAddress })
        setInputValue(displayAddress)
        onChange(displayAddress)
        lastInternalValueRef.current = displayAddress
        onAddressVerified?.(displayAddress)
      } catch {
        setInputValue(fallbackDescription)
        onChange(fallbackDescription)
        lastInternalValueRef.current = fallbackDescription
      }
    },
    [onChange, onPlaceResolved, onAddressVerified]
  )

  const hasSuggestions = suggestions.length > 0
  const statusId = id ? `${id}-status` : 'address-autocomplete-status'
  const showFetchingStatus = isFetching && !hasSuggestions
  const comboboxDescribedBy = [ariaDescribedBy, showFetchingStatus ? statusId : null]
    .filter(Boolean)
    .join(' ') || undefined
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (hasSuggestions) {
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          const s = suggestions[highlightedIndex]
          void handleSelect(s.placeId, s.description)
        }
        return
      }
      e.preventDefault()
      e.currentTarget.blur()
      return
    }
    if (!hasSuggestions) return
    const maxIndex = suggestions.length - 1
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev < maxIndex ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break
      case 'Escape':
        e.preventDefault()
        setSuggestions([])
        setHighlightedIndex(-1)
        break
      default:
        break
    }
  }

  const optionId = (placeId: string, index: number) =>
    `address-option-${(placeId || `index-${index}`).replace(/[^a-zA-Z0-9-_]/g, '-')}`

  return (
    <div ref={wrapperRef} className="relative w-full">
      <input
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        className="flex-1 font-body font-regular text-md leading-md text-text-primary-900 outline-none border-none bg-transparent w-full"
        placeholder={placeholder}
        role="combobox"
        aria-expanded={hasSuggestions}
        aria-autocomplete="list"
        aria-controls="address-autocomplete-listbox"
        aria-activedescendant={
          hasSuggestions && highlightedIndex >= 0 && highlightedIndex < suggestions.length
            ? optionId(suggestions[highlightedIndex].placeId, highlightedIndex)
            : undefined
        }
        aria-invalid={ariaInvalid}
        aria-describedby={comboboxDescribedBy}
        aria-busy={isFetching}
      />

      {showFetchingStatus && (
        <p id={statusId} className="mt-1 font-body text-xs text-text-tertiary-600">
          Searching addresses…
        </p>
      )}

      {hasSuggestions && (
        <ul
          id="address-autocomplete-listbox"
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 bg-background-primary border border-border-primary rounded-lg shadow-lg overflow-hidden"
        >
          {suggestions.map(({ placeId, description }, index) => (
            <li
              key={placeId || `suggestion-${index}`}
              id={optionId(placeId, index)}
              role="option"
              aria-selected={index === highlightedIndex}
              onClick={() => void handleSelect(placeId, description)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-[12px] py-[10px] font-body text-md text-text-primary-900 cursor-pointer hover:bg-background-secondary-hover min-h-[44px] flex items-center ${index === highlightedIndex ? 'bg-background-secondary-hover' : ''}`}
            >
              {description}
            </li>
          ))}
          <li className="px-[12px] py-[6px] font-body text-xs text-text-tertiary-600 text-right">
            Powered by Google
          </li>
        </ul>
      )}
    </div>
  )
}
